import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import Validator from 'mixins/validator';
import Resetter from 'mixins/resetter';
import { Button, Checkbox } from 'react-toolbox';
import { connect } from 'react-redux';
import ComposeWindow from 'components/ComposeWindow';
import styles from 'styles/input.scss';
import RTCheckbox from 'styles/checkbox.scss';
import formStyles from 'styles/form.scss';
import StyledSelect from 'components/StyledSelect';
import TextArea from 'components/TextArea';
import UpdateDueDate from 'components/UpdateDueDate';
import ConfirmDialog from 'components/ConfirmDialog';
import BreakLine from 'components/BreakLine';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import S from 'components/ComposeWindow/styles.scss';
import { addEventToPhase, updatePhase, clearValidation, overridePhaseStatus } from 'actionCreators/phase';
import { updateIMS, updateIMSNote } from 'actionCreators/ims_actions';
import PhaseType from 'types/phase';
import { getPhaseErrors } from '../../../../../../reducers';
import { error, errorSummary, validateSource, deriveStatusName } from '../../../../../../utils';
import { CONTRACTOR_ROLES } from 'config/constants';

const displayDeleteAppt = status => ['hold', 'rescheduling', 'customer_not_ready'].includes(status);

const expand = keyframes`
0% {
    transform: translateY(-100%);
    opacity: 0;
  }

100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const DropdownDiv = styled.div(({ status }) => ({
  animation: displayDeleteAppt(status) ? `${expand} 400ms ease-out` : '',
}));

const ContainerDiv = styled.div`
overflow: hidden;
`;

class AddCommunicationModal extends Resetter(Validator(React.Component)) {
  constructor(props) {
    super(props);
    const { phase = {} } = props;
    const { source = '' } = phase;
    const isContractor = CONTRACTOR_ROLES.includes((props.role || '').toLowerCase());
    const isLhi = validateSource(source);
    const eligibleImsVendor = (isLhi && source !== 'IME') || source === 'IFI';
    const eligibleVendor = eligibleImsVendor || source === 'RF';
    this.state = {
      isContractor,
      isLhi,
      eligibleImsVendor,
      eligibleVendor,
      vendorTag: eligibleImsVendor ? 'IMS' : source,
      status: '',
      note: '',
      dueAt: '',
      errors: {},
      modalActive: false,
      deleteAppts: false,
      statusOptions: this.buildStatusOptions(isContractor, phase),
    };
  }

  static VALIDATIONS = { who: true, method: true, result: true, note: true };

  toggleModalActive = () => this.setState({ modalActive: !this.state.modalActive });

  toggleDeleteAppts = () => this.setState({ deleteAppts: !this.state.deleteAppts })

  toggleCnrCheckbox = () => this.setState({ cnrCheckbox: !this.state.cnrCheckbox },
    () => (this.state.cnrCheckbox ? this.setState({ reschedCheckbox: false }) : null));

  toggleReschedCheckbox = () => this.setState({ reschedCheckbox: !this.state.reschedCheckbox },
    () => (this.state.reschedCheckbox ? this.setState({ cnrCheckbox: false }) : null));

  callUpdatePhase = (id, dueAt) => (dueAt ?
    this.props.updatePhase({ id, due_at: dueAt }, { actionType: 'update_due_at' }) : Promise.resolve());

  callOverride = (dueAt = false) => {
    const { overridePhaseStatus: override, phase: { id } } = this.props;
    const { status, deleteAppts } = this.state;
    const phaseProps = { phase: { status }, event: { data: { note: '' } } };
    const delete_appointments = deleteAppts ? 'yes' : 'no';
    return override(id, phaseProps, { source: 'add communication', delete_appointments })
      .then(() => this.callUpdatePhase(id, dueAt));
  }

  callIms = (id, due_at, params, ims_type = 'follow_up') => {
    const { updateIMS: stdUpdate, updateIMSNote: noteUpdate } = this.props;
    return due_at ? stdUpdate(id, { ...params, ims_type, due_at }) : noteUpdate(id, params);
  }

  handleSubmit = (type = 'Internal') => {
    const { cnrCheckbox, reschedCheckbox } = this.state;
    const note = this.textAreaComponentRef.textAreaRef.value;
    const validations = { note: true, ...((cnrCheckbox || reschedCheckbox) && { dueAt: true }) };
    this.setState({ note }, () => {
      const { addEventToPhase: addEvent, phase: { id } = {} } = this.props;
      const { dueAt, status, eligibleImsVendor } = this.state;
      if (!this.validate(validations)) return;
      const noteProps = {
        name: `${type} Communication`,
        kind: 'communication',
        data: { note, new_due_date: dueAt || null },
      };
      const validExternal = eligibleImsVendor && type === 'External';
      (status ? this.callOverride(dueAt) : this.callUpdatePhase(id, dueAt))
        .then(() => addEvent(id, noteProps))
        .then(() => (validExternal ? this.callIms(id, dueAt, { note }) : Promise.resolve()))
        .then(() => this.handleClose())
        .catch(() => {});
    });
  };

  handleClose() {
    super.handleClose();
    this.props.clearValidation();
  }

  handleChange = prop => val => this.setState({ [prop]: val });

  handleDueChange = dueAt => this.setState({ dueAt });

  buildStatusOptions = (isContractor, {
    type, status, source, available_statuses: statuses = [],
    appointments: appts = [], workroom: { name: workroom = '' } = {} } = {},
  ) => {
    const blockedStatuses = ['canceled', status];
    if (!appts.length) blockedStatuses.push('awaiting_install');
    if (!isContractor && type !== 'Assessment') blockedStatuses.push('completed');
    if (type === 'Installation' && ['installing', 'follow_up', 'completed', 'canceled'].includes(status)) {
      blockedStatuses.push('waiting_for_product');
    }
    return statuses.map(aS => ({
      ...aS, label: deriveStatusName(aS.label, { source, workroom }),
    })).filter(s => !blockedStatuses.includes(s.value));
  };

  noteWarning = () => `Notes that are written here will be uploaded to ${this.state.vendorTag} if external.`;

  canShowExternalButton = () => {
    const { phase = {} } = this.props;
    const { source, type, wo_source } = phase;
    if (type !== 'Workorder') return true;
    if (source !== 'RF') return true;
    // If the phase is a workorder of source "RF", don't show the External button unless wo_source
    // is present & not "SPIG generated"
    return !!wo_source && wo_source !== 'SPIG generated';
  };

  render = () => {
    const { active, serverErrors, phase: { type, status: phaseStatus } } = this.props;
    const { status, dueAt, errors, modalActive, deleteAppts, isContractor, vendorTag,
            statusOptions, eligibleVendor, eligibleImsVendor } = this.state;
    return (<ComposeWindow active={active} title="Add Communication" onClose={this.handleClose}>
      <div className={styles.errored}>
        {serverErrors && Object.keys(serverErrors).length > 0 && (<span className={styles.error}>
          {errorSummary(serverErrors)}
        </span>)}
      </div>
      {modalActive && <ConfirmDialog
        title={`${dueAt ?
          `This sends a Follow Up Date change to ${vendorTag} for the selected date` :
          `This sends a note to ${vendorTag}`}`}
        okLabel="Yes"
        cancelLabel="Cancel"
        cancel={this.toggleModalActive}
        dismiss={this.toggleModalActive}
        proceed={() => this.handleSubmit('External')}
      />}
      <TextArea
        label={this.noteWarning()}
        defaultValue=""
        errors={errors.note}
        autoFocus
        ref={(comp) => { this.textAreaComponentRef = comp; }}
      />
      {!['canceled', 'completed'].includes(phaseStatus) && !isContractor && (<UpdateDueDate
        value={dueAt}
        onChange={this.handleDueChange}
        errors={errors}
        dateLabel="Update Due Date"
        timeLabel="Update Due Date Time"
        required={false}
      />)}
      <StyledSelect
        className={S.select}
        label="Override to Status"
        onChange={this.handleChange('status')}
        options={statusOptions}
        value={status}
        placeholder={'Select'}
        error={error('status', errors)}
      />
      {displayDeleteAppt(status) && <ContainerDiv>
        <DropdownDiv status={status}>
          <div className={formStyles.statusCheckboxRow}>
            <Checkbox
              checked={deleteAppts}
              label={'Delete future appointments due to reschedule status?'}
              theme={RTCheckbox}
              className={formStyles.checkboxListItem}
              onChange={this.toggleDeleteAppts}
            />
          </div>
        </DropdownDiv>
      </ContainerDiv>}
      {!isContractor && (<div className={formStyles.row}>
        <span>Due Date Times earlier than 8am will be set to 8am</span>
      </div>)}
      <div className={formStyles.row}>
        <Button
          label="Internal"
          primary
          flat
          ripple
          className={S.submit}
          onClick={() => this.handleSubmit('Internal')}
        />
        {!isContractor && eligibleVendor && this.canShowExternalButton() && (<Button
          label="External"
          primary
          flat
          ripple
          className={S.submit}
          onClick={this.toggleModalActive}
        />)}
      </div>
      {eligibleImsVendor && type === 'Workorder' && <Fragment>
        <BreakLine />
        <div className={formStyles.addCommWarning}>
          If External, it will need to be manually updated in IMS
        </div>
      </Fragment>}
    </ComposeWindow>);
  }
}

AddCommunicationModal.propTypes = {
  active: PropTypes.bool.isRequired,
  role: PropTypes.string,
  addEventToPhase: PropTypes.func.isRequired,
  updatePhase: PropTypes.func.isRequired,
  overridePhaseStatus: PropTypes.func.isRequired,
  clearValidation: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  phase: PhaseType.isRequired,
  serverErrors: PropTypes.shape({}),
};

AddCommunicationModal.defaultProps = { active: false };

export default connect(
  state => ({
    serverErrors: getPhaseErrors(state),
    role: state.user.role,
  }),
  { addEventToPhase, updatePhase, clearValidation, updateIMS, updateIMSNote, overridePhaseStatus },
)(AddCommunicationModal);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/components/addCommunicationModal.jsx